import { ChangeDetectionStrategy, Component, HostListener, inject, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { from, Observable, of } from 'rxjs';
import {
    delay,
    distinctUntilChanged,
    map,
    shareReplay,
    startWith,
    switchMap,
    take,
    takeUntil,
    withLatestFrom
} from 'rxjs/operators';

import { customReasonOptionValue, ExportDialogOutput, ExportType } from '@common/entities/dialog/export-dialog.entity';
import { ImageEditDialogData, ImageEditDialogOutput } from '@common/entities/dialog/image-edit-dialog.entity';
import { RecordExportTransformation } from '@common/entities/record-export-source.entity';
import { ImageEditDialogComponent } from '@common/modules/imaging/image-cropper-dialog/image-edit-dialog.component';

import { IntellectualRecordsService } from '@common/services/intellectual-records/intellectual-records.service';
import { ExportRequestService, PersonalSelectionService, UserExportLocationsService } from '@mediahaven/shared';
import {
    ApiFragment,
    BaseContainer,
    DialogConfig,
    DialogRef,
    DialogStyle,
    ExportLocation,
    ExportLocationImageOptions,
    ExportRequest,
    ExportSource,
    FormRadioListItem,
    FormSelectOption,
    LocalStorageID,
    LocalStorageStateService,
    MultiFormSelectOptions,
    multiFormSelectOptionWrapper,
    RecordTypeStructure,
    ShDynamicDialogService,
    toFormSelectOption
} from '@stylehaven/components';
import { isEqual } from 'lodash-es';
import { CustomExportReasonValidator } from './custom-export-reason-validator';

interface ExportOptions {
    sources: ExportSource[];
    transformation: string;
    exportLocation: ExportLocation;
    exportReason: string;
}

@Component({
    selector: 'mhv-export-dialog',
    templateUrl: './export-dialog.component.html',
    styleUrls: ['./export-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExportDialogComponent extends BaseContainer implements OnInit {
    private readonly RECORDS: ApiFragment[];
    private readonly PREVIEW_URL: string | null;

    private readonly EXPORT_OPTIONS_LOCAL_STORAGE_ID: LocalStorageID = LocalStorageID.ExportOptions;
    private readonly LAST_EXPORT_OPTIONS: ExportOptions | null;

    // Forms
    public baseForm: UntypedFormGroup;
    public exportReasonForm: UntypedFormGroup;
    public isFormValid$: Observable<boolean>;

    // Messages
    public infoMessage$: Observable<string | null>;

    // Labels
    public sourceLabel$: Observable<string>;
    public transformationsLabel$: Observable<string>;
    public exportLocationLabel$: Observable<string>;
    public exportLocationNoneSelectedLabel$: Observable<string>;

    // Options
    public sourceOptions$: Observable<FormSelectOption[]>;
    public transformationsOptions$: Observable<FormRadioListItem[]>;
    public exportLocationSelectOptions$: Observable<MultiFormSelectOptions<ExportLocation>>;
    public exportReasonSelectOptions$: Observable<MultiFormSelectOptions>;

    private exportLocations$: Observable<ExportLocation[]>;

    // Download button
    public disableDownload = false;
    public disabledDownloadMsg = '';

    // Helpers
    private selectedExportLocation$: Observable<ExportLocation | undefined>;
    private selectedTransformation$: Observable<RecordExportTransformation>;
    public exportReasonRequired$: Observable<boolean>;
    public customReasonAllowed$: Observable<boolean>;

    // Services
    private readonly exportLocationService: UserExportLocationsService = inject(UserExportLocationsService);
    private readonly exportRequestService: ExportRequestService = inject(ExportRequestService);
    private readonly intellectualRecordsService: IntellectualRecordsService = inject(IntellectualRecordsService);
    private readonly personalSelectionService: PersonalSelectionService = inject(PersonalSelectionService);
    private readonly dialogService: ShDynamicDialogService = inject(ShDynamicDialogService);
    private readonly localStorageStateService: LocalStorageStateService = inject(LocalStorageStateService);

    constructor(
        public config: DialogConfig,
        private readonly dialog: DialogRef<ExportDialogOutput>,
        private readonly formBuilder: UntypedFormBuilder
    ) {
        super();
        this.RECORDS = this.config.data.records;
        this.PREVIEW_URL =
            this.RECORDS.length === 1 && this.RECORDS[0].hasPreview() && this.RECORDS[0].isImage()
                ? this.RECORDS[0].getPreviewUrl()
                : null;

        this.LAST_EXPORT_OPTIONS = this.loadLastExportOptions();
    }

    @HostListener('document:keydown.enter', ['$event'])
    handleEnter(event: { target: HTMLInputElement }) {
        // Prevent submitting when form is invalid
        if (!this.baseForm.valid) {
            return;
        }
        this.confirm();
    }

    ngOnInit(): void {
        // Init Form
        this.initForm();

        // Init Data
        this.initTransformationValueStream();
        this.initInfoMessageStream();
        this.initLabelStreams();
        this.initExportLocationStream();

        // Init control options
        this.initSourceOptionsStream();
        this.initTransformationsOptionsStream();
        this.initExportReasonsStreams();
        this.initExportlocationOptionsStream();

        // Init controls
        this.initSourceControl();
        this.initTransformationsControl();
        this.initReasonControl();
    }

    // #region Form
    private initForm(): void {
        this.baseForm = this.formBuilder.group({
            source: [null, Validators.required],
            transformations: [null, Validators.required],
            exportLocations: [null, Validators.required]
        });

        this.exportReasonForm = this.formBuilder.group(
            {
                exportReasons: [null, Validators.required],
                customReason: []
            },
            { validators: [CustomExportReasonValidator] }
        );
    }

    private initTransformationValueStream(): void {
        this.selectedTransformation$ = this.baseForm.controls.transformations.valueChanges.pipe(
            distinctUntilChanged(),
            shareReplay()
        );
    }
    // #endregion

    // #region Info messages
    private initInfoMessageStream(): void {
        const infoMessages$: Observable<string[]> = this.selectedTransformation$.pipe(
            switchMap(transformationsValue => from(this.getInfoMessages(transformationsValue)))
        );
        this.infoMessage$ = infoMessages$.pipe(map(messages => messages.join('<br />')));
    }

    private async getInfoMessages(transformationsValue: RecordExportTransformation): Promise<string[]> {
        const infoMessages: string[] = [];

        const exportType: ExportType =
            transformationsValue === RecordExportTransformation.EXPORT ? ExportType.EXPORT : ExportType.DOWNLOAD;

        if (this.RECORDS.length === 1) {
            infoMessages.push(...(await this.getSingleRecordInfoMessages(exportType)));
        } else {
            infoMessages.push(...this.getBulkRecordInfoMessages(exportType));
        }

        infoMessages.push(...this.getGeneralInfoMessages());
        return infoMessages;
    }

    private async getSingleRecordInfoMessages(exportType: ExportType): Promise<string[]> {
        const infoMessages: string[] = [];

        const record: ApiFragment = this.RECORDS[0];
        const isCollection: boolean = record.isNewCollection();
        const isSelection: boolean = record.isSelection();

        if (!isCollection && !isSelection) {
            if (exportType === ExportType.EXPORT) {
                infoMessages.push(this.translateService.instant(`media.export-dialog.info.export-item`));
            }
            return infoMessages;
        }

        if (isCollection) {
            infoMessages.push(this.translateService.instant(`media.export-dialog.info.${exportType}-collection`));
        }

        if (isSelection) {
            infoMessages.push(this.translateService.instant(`media.export-dialog.info.${exportType}-selection`));
        }

        const nrOfExportableItems: number = isSelection
            ? await this.personalSelectionService.getNrOfExportableChildren(record.getFragmentId()).toPromise()
            : await this.intellectualRecordsService.getNrOfExportableChildren(record.getRecordId()).toPromise();

        const nrOfNonExportableItems: number = isSelection
            ? await this.personalSelectionService.getNrOfNonExportableChildren(record.getFragmentId()).toPromise()
            : await this.intellectualRecordsService.getNrOfNonExportableChildren(record.getRecordId()).toPromise();

        if (nrOfExportableItems === 0) {
            infoMessages.push(this.translateService.instant('media.export-dialog.info.no-export-rights'));

            this.disableDownload = true;
            this.disabledDownloadMsg = this.translateService.instant('media.export-dialog.info.no-export-rights');
        } else if (nrOfNonExportableItems > 0) {
            infoMessages.push(
                this.translateService.instant('media.export-dialog.info.limited-export-rights', {
                    nonExportableItems: nrOfNonExportableItems
                })
            );
        }

        return infoMessages;
    }

    private getBulkRecordInfoMessages(exportType: ExportType): string[] {
        const infoMessages: string[] = [];

        const hasCollections: boolean = this.RECORDS.some(record => record.isNewCollection());

        infoMessages.push(
            this.translateService.instant(`media.export-dialog.info.bulk-${exportType}-items`, {
                amount: this.RECORDS.length
            })
        );

        if (hasCollections) {
            infoMessages.push(this.translateService.instant('media.export-dialog.info.collection-selected'));
        }

        return infoMessages;
    }

    private getGeneralInfoMessages(): string[] {
        const infoMessages: string[] = [];

        const hasDataObjects: boolean = this.RECORDS.some(
            record => record.getRecordStructure() === RecordTypeStructure.Data
        );
        const hasNonCollectionOrSelectionIntellectualObjects: boolean = this.RECORDS.some(
            record => record.isIntellectualObject() && !record.isNewCollection() && !record.isSelection()
        );

        // Only show this with Intellectual RecordTypes that are not Mh2Collection or Selection
        if (hasNonCollectionOrSelectionIntellectualObjects) {
            infoMessages.push(
                this.translateService.instant('media.export-dialog.info.export-intellectual-records', {
                    amount: this.RECORDS.length
                })
            );
        }

        if (hasDataObjects) {
            infoMessages.push(this.translateService.instant('media.export-dialog.info.export-data-objects'));
        }

        return infoMessages;
    }
    // #endregion

    // #region Labels
    private initLabelStreams(): void {
        this.sourceLabel$ = of(
            this.translateService.instant(`media.export-dialog.export-source.label`, { amount: this.RECORDS.length })
        );
        this.transformationsLabel$ = of(
            this.translateService.instant('media.export-dialog.export-transformations.label')
        );

        this.exportLocationLabel$ = this.selectedTransformation$.pipe(
            map(value =>
                value === RecordExportTransformation.EXPORT
                    ? `media.export-dialog.format.export.label`
                    : `media.export-dialog.format.download.label`
            ),
            map(translateKey => this.translateService.instant(translateKey))
        );

        this.exportLocationNoneSelectedLabel$ = this.selectedTransformation$.pipe(
            map(value =>
                value === RecordExportTransformation.EXPORT
                    ? 'media.export-dialog.format.choose-exportlocation'
                    : 'media.export-dialog.format.choose-format'
            ),
            map(translateKey => this.translateService.instant(translateKey))
        );
    }
    // #endregion

    // #region Source
    private initSourceOptionsStream(): void {
        this.sourceOptions$ = of([
            {
                id: 'preview',
                label: this.translateService.instant('media.export-dialog.export-source.options.preview'),
                value: ExportSource.ACCESS
            },
            {
                id: 'original',
                label: this.translateService.instant('media.export-dialog.export-source.options.original'),
                value: ExportSource.ORIGINAL
            }
        ]);
    }

    private initSourceControl(): void {
        const initialSources = this.LAST_EXPORT_OPTIONS?.sources ?? [ExportSource.ORIGINAL];
        this.baseForm.controls.source.setValue(initialSources);
    }
    // #endregion

    // #region Transformations
    private initTransformationsOptionsStream(): void {
        const transformationOptions: FormRadioListItem[] = [
            {
                id: RecordExportTransformation.DIRECT,
                label: this.translateService.instant('media.export-dialog.export-transformations.direct'),
                value: RecordExportTransformation.DIRECT
            }
        ];

        if (this.PREVIEW_URL) {
            const cropOption: FormRadioListItem = {
                id: RecordExportTransformation.CROPPED,
                label: this.translateService.instant('media.export-dialog.export-transformations.cropped'),
                value: RecordExportTransformation.CROPPED
            };

            transformationOptions.push(cropOption);
        }

        const transformedOption: FormRadioListItem = {
            id: RecordExportTransformation.TRANSFORMED,
            label: this.translateService.instant('media.export-dialog.export-transformations.transformed'),
            value: RecordExportTransformation.TRANSFORMED
        };

        transformationOptions.push(transformedOption);

        const exportOption: FormRadioListItem = {
            id: RecordExportTransformation.EXPORT,
            label: this.translateService.instant('media.export-dialog.export-transformations.export'),
            value: RecordExportTransformation.EXPORT
        };

        this.transformationsOptions$ = this.exportLocationService.externalLocations$.pipe(
            map(externalLocations =>
                externalLocations.length > 0 ? [...transformationOptions, exportOption] : transformationOptions
            )
        );
    }

    private initTransformationsControl(): void {
        this.initTransformationsOptionsListener();
        this.baseForm.controls.transformations.setValue(this.initialTransformation);
    }

    private initTransformationsOptionsListener(): void {
        this.selectedTransformation$
            .pipe(withLatestFrom(this.transformationsOptions$), takeUntil(this.destroy$))
            .subscribe(([value, transformationOptions]) => {
                const options: string[] = transformationOptions.map(option => option.value.toString());

                // Safeguard against unavailable transformation options
                if (!options.includes(value)) {
                    this.baseForm.controls.transformations.setValue(RecordExportTransformation.DIRECT);
                    return;
                }

                // Toggle ExportLocation selection
                if (value === RecordExportTransformation.TRANSFORMED || value === RecordExportTransformation.EXPORT) {
                    this.baseForm.controls.exportLocations.enable();
                } else {
                    this.baseForm.controls.exportLocations.disable();
                }

                // Always set to default ExportLocation on Direct and Cropped downloads
                if (value === RecordExportTransformation.DIRECT || value === RecordExportTransformation.CROPPED) {
                    this.setDefaultExportLocation(value);
                    return;
                }

                // Restore exportlocation only if transformation matches
                if (value === this.LAST_EXPORT_OPTIONS?.transformation) {
                    const initialExportLocation = this.LAST_EXPORT_OPTIONS?.exportLocation;
                    this.baseForm.controls.exportLocations.setValue([initialExportLocation]);
                    return;
                }

                // If set to Export and none of above conditions apply, clear the set ExportLocation
                // This is to prevent non-external ExportLocations remaining selected
                if (value === RecordExportTransformation.EXPORT) {
                    this.baseForm.controls.exportLocations.setValue([]);
                }
            });
    }
    // #endregion

    // #region Export locations
    private initExportLocationStream(): void {
        this.exportLocations$ = this.baseForm.controls.transformations.valueChanges.pipe(
            distinctUntilChanged(),
            startWith(this.initialTransformation),
            switchMap(transformControlValue =>
                transformControlValue === RecordExportTransformation.EXPORT
                    ? this.exportLocationService.externalLocations$
                    : this.exportLocationService.downloadableLocations$
            ),
            shareReplay(1)
        );
    }

    private initExportlocationOptionsStream(): void {
        this.exportLocationSelectOptions$ = this.exportLocations$.pipe(
            map((exportLocations: ExportLocation[]) => {
                const formSelectOptions: FormSelectOption<ExportLocation>[] = exportLocations
                    .sort((first, second) => first.Name.localeCompare(second.Name))
                    .map(exportLocation => ({ label: exportLocation.Name, value: exportLocation }));

                return multiFormSelectOptionWrapper(formSelectOptions);
            })
        );
    }

    private setDefaultExportLocation(transformControlValue: RecordExportTransformation): void {
        if (transformControlValue === RecordExportTransformation.EXPORT) {
            return;
        }

        this.exportLocationService
            .getDefaultExportLocation()
            // TODO: find a way to remove delay: cached value = too fast -> disables 'confirm' button
            .pipe(delay(200), take(1), takeUntil(this.destroy$))
            .subscribe((defaultExportLocation: ExportLocation) => {
                const cleanDefaultExportLocation: ExportLocation = {
                    ...defaultExportLocation,
                    ExportReasons: defaultExportLocation.ExportReasons?.length
                        ? defaultExportLocation.ExportReasons
                        : null
                };

                this.baseForm.controls.exportLocations.setValue([cleanDefaultExportLocation]);
            });
    }
    // #endregion

    // #region Export reason
    private initExportReasonsStreams(): void {
        this.selectedExportLocation$ = this.baseForm.controls.exportLocations.valueChanges.pipe(
            distinctUntilChanged(isEqual),
            map(() =>
                this.baseForm.controls.exportLocations.value
                    ? this.baseForm.controls.exportLocations.value[0]
                    : undefined
            ),
            shareReplay(1),
            takeUntil(this.destroy$)
        );

        this.exportReasonRequired$ = this.selectedExportLocation$.pipe(
            map(exportLocation => !!exportLocation && exportLocation?.ExportReasonRequired)
        );

        this.exportReasonSelectOptions$ = this.selectedExportLocation$.pipe(
            map(exportLocation => this.getExportReasonFormOptions(exportLocation))
        );

        this.customReasonAllowed$ = this.selectedExportLocation$.pipe(
            map(
                exportLocation =>
                    !!exportLocation &&
                    exportLocation?.ExportReasonRequired &&
                    exportLocation?.CustomExportReasonAllowed
            )
        );

        this.exportReasonForm.controls.exportReasons.valueChanges
            .pipe(
                map(value => !!value && value.length > 0 && value[0] === customReasonOptionValue),
                takeUntil(this.destroy$)
            )
            .subscribe(enableCustomReasonControl =>
                enableCustomReasonControl
                    ? this.exportReasonForm.controls.customReason.enable()
                    : this.exportReasonForm.controls.customReason.disable()
            );

        this.selectedExportLocation$
            .pipe(takeUntil(this.destroy$))
            .subscribe((exportLocation: ExportLocation | undefined) =>
                this.setExportReasonControlsState(exportLocation)
            );
    }

    private getExportReasonFormOptions(exportLocation: ExportLocation | undefined): MultiFormSelectOptions {
        if (!exportLocation) {
            return multiFormSelectOptionWrapper([]);
        }

        const exportReasons: FormSelectOption[] = exportLocation.ExportReasons
            ? exportLocation.ExportReasons.map(reason => toFormSelectOption(reason))
            : [];

        if (exportLocation.CustomExportReasonAllowed) {
            const customOption: FormSelectOption = {
                label: this.translateService.instant('media.export-dialog.export-reason.custom'),
                value: customReasonOptionValue
            };

            exportReasons.push(customOption);
        }

        return multiFormSelectOptionWrapper(exportReasons);
    }

    private setExportReasonControlsState(exportLocation: ExportLocation | undefined): void {
        this.exportReasonForm.reset();
        if (exportLocation && exportLocation.ExportReasonRequired) {
            this.exportReasonForm.enable();
        } else {
            this.exportReasonForm.disable();
        }
    }

    private initReasonControl(): void {
        const initialReason = this.LAST_EXPORT_OPTIONS?.exportReason;
        this.exportReasonForm.controls.exportReasons.setValue([initialReason]);
    }
    // #endregion

    // #region Action handlers
    public confirm(): void {
        this.baseForm.controls.transformations.value === RecordExportTransformation.CROPPED
            ? this.openImageEditDialog()
            : this.prepareExport();
    }

    public cancel(): void {
        const output: ExportDialogOutput = { type: 'cancel' };
        this.dialog.close(output);
    }
    // #endregion

    // #region Edit image
    private openImageEditDialog(): void {
        const width: number | null = this.RECORDS[0].getWidth();
        const height: number | null = this.RECORDS[0].getHeight();

        if (!this.PREVIEW_URL || !width || !height) {
            console.error(
                `ExportDialogComponent:: PreviewUrl, Width or Height could not be found on record ${this.RECORDS[0].getRecordId()}`
            );
            return;
        }

        const data: ImageEditDialogData = {
            ok: this.translateService.instant('actions.download.prepare'),
            cancel: this.translateService.instant('actions.common.cancel'),
            previewUrl: this.PREVIEW_URL,
            width,
            height
        };

        const ref$: DialogRef<ImageEditDialogOutput> = this.dialogService.open(ImageEditDialogComponent, {
            title: this.translateService.instant('media.export-dialog.export-transformations.cropped'),
            data,
            options: {
                style: DialogStyle.ExtraExtraWide
            }
        });

        ref$.afterClosed$
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(output => this.openImageEditDialogOnSuccess(output));
    }

    private openImageEditDialogOnSuccess(output: ImageEditDialogOutput): void {
        if (output.type === 'success') {
            this.prepareExport(output.imageOptions);
        }
    }
    // #endregion

    // #region Download/export
    private prepareExport(imageTransformation?: ExportLocationImageOptions): void {
        const exportLocation: ExportLocation = this.baseForm.controls.exportLocations.value[0];

        const exportSourceValues: ExportSource[] = this.baseForm.controls.source.value;
        const exportSource: ExportSource =
            exportSourceValues.length > 1 ? ExportSource.ORIGINAL_AND_ACCESS : exportSourceValues[0];

        const exportDirect: boolean =
            this.baseForm.controls.transformations.value !== RecordExportTransformation.EXPORT;

        const exportReasonSelection: string | undefined = this.exportReasonForm.controls.exportReasons.value
            ? this.exportReasonForm.controls.exportReasons.value[0]
            : undefined;
        const customReason: string | undefined = this.exportReasonForm.controls.customReason.value;
        const exportReason: string | undefined =
            exportReasonSelection === customReasonOptionValue ? customReason : exportReasonSelection;

        const exportRequest: ExportRequest = this.exportRequestService.exportLocationToExportRequest(
            exportLocation,
            this.RECORDS,
            exportSource,
            exportReason,
            imageTransformation
        );

        this.saveLastExportOptions(exportSourceValues, exportLocation, exportReasonSelection);

        const output: ExportDialogOutput = {
            exportRequest: exportRequest,
            exportDirect
        };
        this.dialog.close(output);
    }

    // #endregion

    // #region Last Export Options
    private get initialTransformation(): string {
        return this.LAST_EXPORT_OPTIONS?.transformation ?? RecordExportTransformation.DIRECT;
    }

    private loadLastExportOptions(): ExportOptions | null {
        return this.localStorageStateService.getState(LocalStorageID.ExportOptions);
    }

    private saveLastExportOptions(
        sources: ExportSource[],
        exportLocation: ExportLocation,
        exportReason: string | undefined
    ): void {
        const transformation = this.baseForm.controls.transformations.value;

        const exportOptions = {
            sources,
            transformation,
            exportLocation,
            exportReason
        };

        this.localStorageStateService.setState(this.EXPORT_OPTIONS_LOCAL_STORAGE_ID, exportOptions);
    }
    // #endregion
}
